import axios from 'axios'

const { createKeyedPromiseCache } = require('./site-request.cjs')

const WEBSITE_API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/web2-api/open/website'
  : '/api/open/website'
const NEWS_API_BASE_URL = `${WEBSITE_API_BASE_URL}/news`
const SOLUTIONS_API_BASE_URL = `${WEBSITE_API_BASE_URL}/solutions`
const SUPPORT_API_BASE_URL = `${WEBSITE_API_BASE_URL}/support`
const EXHIBITIONS_API_BASE_URL = `${WEBSITE_API_BASE_URL}/exhibitions`
const HOME_BANNERS_API_BASE_URL = `${WEBSITE_API_BASE_URL}/home-banners`
const CONTACT_WAY_API_BASE_URL = `${WEBSITE_API_BASE_URL}/contact-way/current`
const DEFAULT_SITE_LANGUAGE_API_BASE_URL = `${WEBSITE_API_BASE_URL}/default-site-language`
const EN_QUERY_API_BASE_URL = '/openapi/en'
const USE_EN_PROXY = process.env.VUE_APP_USE_EN_PROXY === 'true'

function isEnSite(siteType) {
  return String(siteType).toUpperCase() === 'EN'
}

function getQueryConfig(siteType, params = {}) {
  if (USE_EN_PROXY && isEnSite(siteType)) {
    return { params }
  }

  return { params: { ...params, siteType } }
}

function getQuery(apiClient, siteType, enPath, fallbackPath, params = {}) {
  const useEnProxy = USE_EN_PROXY && isEnSite(siteType)
  return (useEnProxy ? enQueryApi : apiClient).get(
    useEnProxy ? enPath : fallbackPath,
    getQueryConfig(siteType, params)
  )
}

const api = axios.create({
  baseURL: NEWS_API_BASE_URL,
  timeout: 10000
})

api.interceptors.response.use(
  response => response.data,
  error => {
    console.error('API请求错误:', error)

    if (error.response) {
      const { status, data } = error.response
      switch (status) {
        case 403:
          console.error('没有权限访问')
          break
        case 404:
          console.error('请求的资源不存在')
          break
        case 500:
          console.error('服务器内部错误')
          break
        default:
          console.error(`请求失败: ${status}`)
      }
      return Promise.reject(data || error.message)
    }

    if (error.request) {
      console.error('网络连接错误')
      return Promise.reject(new Error('网络连接错误，请检查网络设置'))
    }

    return Promise.reject(error)
  }
)

const enQueryApi = axios.create({
  baseURL: EN_QUERY_API_BASE_URL,
  timeout: 10000
})

enQueryApi.interceptors.response.use(
  response => response.data,
  error => Promise.reject((error.response && error.response.data) || error)
)

export const newsAPI = {
  getNewsList(params = {}, siteType = 'EN') {
    return getQuery(api, siteType, '/news', '', params)
  },

  getNewsCategories(siteType = 'EN') {
    return getQuery(api, siteType, '/news/categories', '/categories')
  },

  getFeaturedNews(featuredType, siteType = 'EN') {
    return getQuery(api, siteType, '/news/featured', '/featured', { featuredType })
  },

  getNewsDetail(urlSlug, siteType = 'EN') {
    const encodedSlug = encodeURIComponent(String(urlSlug))
    return getQuery(api, siteType, `/news/${encodedSlug}`, `/${encodedSlug}`)
  },

  getNewsDetailById(id, siteType = 'EN') {
    return api.get(`/id/${encodeURIComponent(String(id))}`, {
      params: { siteType }
    })
  },

  increaseViewCount(urlSlug, siteType = 'EN') {
    return api.post(`/${encodeURIComponent(String(urlSlug))}/view`, null, {
      params: { siteType }
    })
  }
}

const solutionApi = axios.create({
  baseURL: SOLUTIONS_API_BASE_URL,
  timeout: 10000
})

solutionApi.interceptors.response.use(
  response => response.data,
  error => Promise.reject((error.response && error.response.data) || error)
)

export const solutionAPI = {
  getCategories(siteType = 'EN') {
    return getQuery(solutionApi, siteType, '/industries', '')
  },

  getTopics(categoryId, siteType = 'EN') {
    const encodedCategoryId = encodeURIComponent(String(categoryId))
    return getQuery(solutionApi, siteType, `/industries/${encodedCategoryId}/topics`, `/${encodedCategoryId}/topics`)
  },

  getTopicDetail(categoryId, topicId, siteType = 'EN') {
    const encodedCategoryId = encodeURIComponent(String(categoryId))
    const encodedTopicId = encodeURIComponent(String(topicId))
    return getQuery(solutionApi, siteType, `/industries/${encodedCategoryId}/topics/${encodedTopicId}`, `/${encodedCategoryId}/topics/${encodedTopicId}`)
  }
}

const supportApi = axios.create({
  baseURL: SUPPORT_API_BASE_URL,
  timeout: 10000
})

supportApi.interceptors.response.use(
  response => response.data,
  error => Promise.reject((error.response && error.response.data) || error)
)

export const supportAPI = {
  getDevices(siteType = 'EN') {
    return getQuery(supportApi, siteType, '/support/devices', '/devices')
  },

  getDevice(model, siteType = 'EN') {
    const encodedModel = encodeURIComponent(String(model))
    return getQuery(supportApi, siteType, `/support/devices/${encodedModel}`, `/devices/${encodedModel}`)
  },

  getFaqs(siteType = 'EN') {
    return getQuery(supportApi, siteType, '/support/faqs', '/faqs')
  }
}

const exhibitionApi = axios.create({
  baseURL: EXHIBITIONS_API_BASE_URL,
  timeout: 10000
})

exhibitionApi.interceptors.response.use(
  response => response.data,
  error => Promise.reject((error.response && error.response.data) || error)
)

export const exhibitionAPI = {
  getSchedule(siteType = 'EN') {
    return getQuery(exhibitionApi, siteType, '/exhibitions', '')
  }
}

const homeBannerApi = axios.create({
  baseURL: HOME_BANNERS_API_BASE_URL,
  timeout: 10000
})

homeBannerApi.interceptors.response.use(
  response => response.data,
  error => Promise.reject((error.response && error.response.data) || error)
)

export const bannerAPI = {
  getHomeBanners(siteType = 'EN') {
    return getQuery(homeBannerApi, siteType, '/home-banners', '')
  }
}

const contactWayApi = axios.create({
  baseURL: CONTACT_WAY_API_BASE_URL,
  timeout: 10000
})

contactWayApi.interceptors.response.use(
  response => response.data,
  error => Promise.reject((error.response && error.response.data) || error)
)

const getCurrentContactWay = createKeyedPromiseCache((siteType) => (
  getQuery(contactWayApi, siteType, '/contact-way/current', '')
    .then(response => response?.data ?? response)
))

export const contactWayAPI = {
  getCurrent(siteType = 'EN') {
    return getCurrentContactWay(siteType)
  }
}

const defaultSiteLanguageApi = axios.create({
  baseURL: DEFAULT_SITE_LANGUAGE_API_BASE_URL,
  timeout: 1500
})

defaultSiteLanguageApi.interceptors.response.use(
  response => response.data,
  error => Promise.reject((error.response && error.response.data) || error)
)

export const siteLanguageAPI = {
  getDefault() {
    return defaultSiteLanguageApi.get('').then(response => response?.data ?? response)
  }
}

export default api
